import type { InstanceId } from '@cognite/apm-client';
import type { EdgeDefinition } from '@cognite/sdk';

import type { IdmAnnotation } from '../file-viewer-modal/types';

import type { ImageItemMetadata } from './types';

export const createFileFromBlobMetadata = (
  blob: Blob,
  metadata: Record<string, string> | undefined
): File => {
  const fileName = metadata?.fileName ?? metadata?.name ?? '';

  return new File([blob], fileName, { type: blob.type });
};

export type ImageMetadataRecord = {
  author: string;
  description?: string;
  fileName?: string;
  source?: string;
  observationExternalId?: string;
};

export const imageMetadataToRecord = (
  metadata: ImageItemMetadata,
  author: string
): ImageMetadataRecord => {
  const result: ImageMetadataRecord = { author };

  if (metadata.description !== undefined) {
    result.description = metadata.description;
  }
  if (metadata.fileName !== undefined) {
    result.fileName = metadata.fileName;
  }
  if (metadata.source !== undefined) {
    result.source = metadata.source;
  }
  if (metadata.observationExternalId !== undefined) {
    result.observationExternalId = metadata.observationExternalId;
  }

  return result;
};

export type FdmFileUpsertWithAssets = {
  name: string;
  mimeType: string;
  externalId: string;
  metadata: Record<string, string>;
  assets: InstanceId[];
  space: string;
};

export const createFdmFileForUpsert = (params: {
  name: string;
  mimeType: string;
  externalId: string;
  metadata: Record<string, string>;
  assets: InstanceId[];
  space: string;
}): FdmFileUpsertWithAssets => params;

export type AnnotationViewReference = {
  externalId: string;
  space: string;
  version: string;
};

type AnnotationEdgeItem = {
  instanceType?: string;
  properties?: Record<string, Record<string, unknown>>;
};

const isEdgeDefinition = (item: AnnotationEdgeItem): item is EdgeDefinition =>
  item.instanceType === 'edge';

export const mapAnnotationEdgesToIdmAnnotations = (
  annotationItems: AnnotationEdgeItem[],
  viewReference: AnnotationViewReference
): IdmAnnotation[] =>
  annotationItems
    .filter((annotation) => annotation.properties)
    .filter(isEdgeDefinition)
    .map((annotationEdge) => ({
      type: annotationEdge.type,
      startNode: annotationEdge.startNode,
      endNode: annotationEdge.endNode,
      externalId: annotationEdge.externalId,
      properties:
        annotationEdge.properties![viewReference.space][
          `${viewReference.externalId}/${viewReference.version}`
        ],
    }));
